import { useCallback } from 'react';

import { loadUserGroups, UserGroup, UserGroups } from '../../../queries/groups';
import { NominatedGroup, NomineeGroupType } from '../../../schemas/nominations';

// Page size matches the user search endpoint's default so the user and group pickers
// load the same number of options per page.
const PAGE_SIZE = 20;

const sectionForGroupType: Record<NomineeGroupType, keyof UserGroups> = {
	department: 'departments',
	location: 'locations',
	team: 'teams',
};

// Default to `[]` so `NominatedGroup.userIds` matches its schema contract (`z.array(...).default([])`)
// even if the server omits the field for groups whose membership it doesn't pre-compute.
const userGroupToNominatedGroup = (group: UserGroup, groupType: NomineeGroupType): NominatedGroup => ({
	groupType,
	key: group.key,
	displayName: group.name,
	userIds: group.userIds ?? [],
});

const normalize = (s: string) => s.toLowerCase().trim();

const matchesQuery = (group: UserGroup, query: string) => {
	if (!query) return true;
	const q = normalize(query);
	return normalize(group.name).includes(q) || normalize(group.slug).includes(q);
};

/**
 * Paginated search over the company's group catalog for nominee selection.
 *
 * Group selection is restricted to `department` / `location` / `team`, and the full
 * catalog is already preloaded by `loadUserGroups()` (called when the nomination
 * modal opens). Since the catalog is in memory, we can serve both the initial open
 * (empty query) and infinite-scroll pages locally — no extra network requests, and
 * we don't need to touch the shared `/v2/users/autocomplete` endpoint used by the
 * give-box.
 *
 * The returned async function takes `(query, offset)` so it can be passed directly
 * as `onSearch` (offset=0) or `onLoadMore` (offset=N) to `SearchableSelect`.
 */
export const useSearchNomineeGroups = (groupType: NomineeGroupType) => {
	return useCallback(
		async (query: string, offset = 0): Promise<NominatedGroup[]> => {
			const groups = await loadUserGroups();
			const section = sectionForGroupType[groupType];
			const candidates = (groups[section] ?? []).filter((g) => matchesQuery(g, query));
			return candidates.slice(offset, offset + PAGE_SIZE).map((g) => userGroupToNominatedGroup(g, groupType));
		},
		[groupType]
	);
};
